import React from "react";
import ReactEcharts from "echarts-for-react";

import { getParameterUnit, getRelativeTime, getObservationInPreferredUnits, getLatestObservation } from "../../utils/helpers";

const sortAscending = (a, b) => new Date(a.time) - new Date(b.time);

const timeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
};

/**
 * Format and ISO 8601 time string to only show hour and minute
 * @param {string} time 
 * @returns {string}
 */
const formatTime = (time) => {
    return new Date(time).toLocaleString("en-US", timeFormatOptions);
};

export const ParameterChart = (props) => {
    const { parameter, parameterObservations, stationMetadata, simpleChart, preferredUnitSystem } = props;

    const sortedObservations = parameterObservations
        .sort(sortAscending);

    const canonicalUnit = getParameterUnit(stationMetadata, parameter);
    const timeData = sortedObservations.map((obs) => obs.time);

    const convertedObservations = sortedObservations.map((obs) => {
        return getObservationInPreferredUnits(parameter, obs[parameter], canonicalUnit, preferredUnitSystem);
    });

    const seriesData = convertedObservations.map((obs) => obs?.value);

    // should be the same for all items, grab the first one
    const displayUnit = convertedObservations[0]?.unit;

    // for simple charts, we only want to show the first and last timestamp, as relative time
    // for full charts, we want to show all the timestamps as hour:minute am/pm
    const xAxisData = timeData.map((time, index) => {
        if (!simpleChart) {
            return formatTime(time);
        } else {
            if (index === 0 || index === timeData.length - 1)  {
                return getRelativeTime(time);
            } else {
                return '';
            }
        }
    });

    // getting the (but not in the future) rather than just the last "observation" because for tides, 
    // we want to show the last time the tide was observed, not future predictions
    const latestDataPoint = getLatestObservation(parameterObservations, parameter);
    
    // find the matching index in the current time array
    const latestIdx = latestDataPoint ? timeData.findIndex(t => t === latestDataPoint.time) : -1;

    // get the converted value that matches your current series units
    // Option A: pull it from `seriesData` at the same index (already converted)
    const latestConvertedValue = latestIdx >= 0 ? seriesData[latestIdx] : null;

    const hasLatest = latestIdx >= 0 && latestConvertedValue != null;

    const calculateChartMax = (value) => {
        return parseFloat(value.max.toFixed(2));
    };

    const calculateChartMin = (value) => {
        return parseFloat(value.min.toFixed(2));
    };

    const chartOption = {
        color: ["#5583aa"],
        grid: {
            containLabel: simpleChart ? false : true,
            // this makes sure labels aren't cut off but seems to add more whitespace than desired
        },
        xAxis: [
            {
                type: 'category',
                data: xAxisData,
                axisLabel: {
                    rotate: -45,
                    showMaxLabel: simpleChart ? true : false,
                }
            }
        ],
        yAxis: [
            {
                type: 'value',
                show: simpleChart ? false : true,
                axisLabel : {
                    formatter: `{value}${displayUnit}`,
                },
                min: calculateChartMin,
                max: calculateChartMax
            }
        ],
        series: [
            {
                data: seriesData,
                type: 'line',
                // special marker for the latest value
                markPoint: hasLatest ? {
                    data: [{
                        name: 'latest',
                        coord: [latestIdx, latestConvertedValue], // index of time axis, value
                        symbol: 'circle',
                        symbolSize: 12,
                        itemStyle: {
                            color: '#E53E3E'
                        }
                    }]
                } : {},
            },
        ],
    };

    // negative margin to account for the extra space containLabel: true option adds. There may be a better way...
    // also bothers me to no end that the prop is 'option' instead of 'options' lol
    return (
        <ReactEcharts option={chartOption} style={{ marginLeft: -25 }} notMerge={true} />
    );
};
